import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  Paper,
  FormControl,
  FormLabel,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';
import {
  browserTimeZone,
  datePickerToCalendarValue,
  formatCalendarOrLocalDay,
} from '../../utils/dateUtils';
import { SoapPlan, SoapTreatment, SoapProcedure } from '../../types/soap';

interface PlanSectionProps {
  data: SoapPlan;
  onChange: (data: SoapPlan) => void;
  disabled?: boolean;
  /** Vaccines found among the treatments and medications (catalog names), not yet recorded. */
  detectedVaccines?: string[];
  /** Entered names (lower-case) that are vaccines, for the row chip. */
  vaccineEntryNames?: Set<string>;
  onRecordVaccinations?: () => void;
  /** The clinic's price-list vaccines then medications, offered for the treatment name (#652). */
  treatmentOptions?: { label: string; group: string }[];
}

interface TreatmentDialogData {
  name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  route?: string;
  notes?: string;
}

interface ProcedureDialogData {
  name: string;
  notes?: string;
}

/**
 * The stored follow-up value -> the Date the picker should display.
 *
 * The picker wants a Date whose LOCAL y/m/d is the day the value means, and
 * which day that is depends on the shape it arrived in — the same split
 * `SoapNoteCard` renders, resolved by the same helper so the two screens can
 * never show different days for one note. A DAY (`yyyy-MM-ddT12:00:00Z` or a
 * bare `yyyy-MM-dd`) is read by its date PART and never re-zoned; `new Date(iso)`
 * would push the noon anchor to the NEXT day past UTC+12. A MOMENT (an older iOS
 * build posts one, DATE-KINDS §2.5 W8) is read as the day it falls on in the
 * VIEWER's zone.
 *
 * ⚠ This matters more here than on the card: whatever the picker displays is
 * what a re-save PERSISTS (`datePickerToCalendarValue` reads the picker's local
 * y/m/d). Showing the wrong day here would write the wrong day.
 */
function followUpPickerValue(
  value: SoapPlan['followUpDate'],
  browserZone: string,
): Date | null {
  if (!value) return null;
  const ymd = formatCalendarOrLocalDay(value, browserZone, 'yyyy-MM-dd');
  if (!ymd) return null;
  const day = parseISO(ymd);
  return isNaN(day.getTime()) ? null : day;
}

const PlanSection: React.FC<PlanSectionProps> = ({
  data,
  onChange,
  disabled = false,
  detectedVaccines = [],
  vaccineEntryNames,
  onRecordVaccinations,
  treatmentOptions = [],
}) => {
  // Only for reading back a LEGACY iOS instant as a day (see
  // `followUpPickerValue`). What this section WRITES is always the anchor.
  const browserZone = React.useMemo(() => browserTimeZone(), []);
  const [treatmentDialog, setTreatmentDialog] = useState(false);
  const [editingTreatmentIndex, setEditingTreatmentIndex] = useState<number | null>(null);
  const [treatmentData, setTreatmentData] = useState<TreatmentDialogData>({
    name: '',
  });
  const [newDiagnostic, setNewDiagnostic] = useState('');
  const [procedureDialog, setProcedureDialog] = useState(false);
  const [editingProcedureIndex, setEditingProcedureIndex] = useState<number | null>(null);
  const [procedureData, setProcedureData] = useState<ProcedureDialogData>({
    name: '',
  });

  // Standardize route display
  const standardizeRouteDisplay = (route: string): string => {
    if (!route) return '-';
    
    const routeMap: Record<string, string> = {
      'iv': 'IV',
      'im': 'IM',
      'sc': 'SC',
      'sq': 'SC',
      'po': 'PO',
      'pr': 'PR',
      'ia': 'IA',
      'in': 'IN',
      'io': 'IO',
      'epi': 'EPI',
      'td': 'TD',
      'top': 'TD',
      'ou': 'OU',
      'od': 'OD',
      'os': 'OS',
      'au': 'AU',
      'ad': 'AD',
      'as': 'AS',
      'intravenous': 'IV',
      'intramuscular': 'IM',
      'subcutaneous': 'SC',
      'oral': 'PO',
      'per os': 'PO',
      'per rectum': 'PR',
      'intra-articular': 'IA',
      'intranasal': 'IN',
      'intraosseous': 'IO',
      'epidural': 'EPI',
      'topical': 'TD',
      'transdermal': 'TD',
    };
    
    return routeMap[route.toLowerCase()] || route;
  };

  // Treatment management
  const handleOpenTreatmentDialog = (treatment?: SoapTreatment, index?: number, source?: string) => {
    if (treatment && index !== undefined) {
      setTreatmentData(treatment);
      setEditingTreatmentIndex(index);
    } else {
      setTreatmentData({ name: '' });
      setEditingTreatmentIndex(null);
    }
    setTreatmentDialog(true);
  };

  const handleSaveTreatment = () => {
    if (!treatmentData.name.trim()) return;

    const treatments = (Array.isArray(data.treatments) ? data.treatments : []);
    let updatedTreatments;

    if (editingTreatmentIndex !== null) {
      updatedTreatments = [...treatments];
      updatedTreatments[editingTreatmentIndex] = treatmentData as SoapTreatment;
    } else {
      updatedTreatments = [...treatments, treatmentData as SoapTreatment];
    }

    onChange({
      ...data,
      treatments: updatedTreatments,
    });

    setTreatmentDialog(false);
    setTreatmentData({ name: '' });
    setEditingTreatmentIndex(null);
  };

  const handleDeleteTreatment = (index: number, source?: string) => {
    if (source === 'treatments') {
      const treatments = (Array.isArray(data.treatments) ? data.treatments : []);
      onChange({
        ...data,
        treatments: treatments.filter((_, i) => i !== index),
      });
    } else if (source === 'medications') {
      const medications = (Array.isArray(data.medicationsPrescribed) ? data.medicationsPrescribed : []);
      onChange({
        ...data,
        medicationsPrescribed: medications.filter((_, i) => i !== index),
      });
    } else {
      // Fallback: remove from both arrays if source is unclear
      const treatments = (Array.isArray(data.treatments) ? data.treatments : []);
      const medications = (Array.isArray(data.medicationsPrescribed) ? data.medicationsPrescribed : []);
      onChange({
        ...data,
        treatments: treatments.filter((_, i) => i !== index),
        medicationsPrescribed: medications.filter((_, i) => i !== index),
      });
    }
  };

  // Diagnostics management
  const handleAddDiagnostic = () => {
    if (newDiagnostic.trim()) {
      const diagnostics = (Array.isArray(data.diagnosticsOrdered) ? data.diagnosticsOrdered : []);
      onChange({
        ...data,
        diagnosticsOrdered: [...diagnostics, newDiagnostic.trim()],
      });
      setNewDiagnostic('');
    }
  };

  const handleRemoveDiagnostic = (index: number) => {
    const diagnostics = (Array.isArray(data.diagnosticsOrdered) ? data.diagnosticsOrdered : []);
    onChange({
      ...data,
      diagnosticsOrdered: diagnostics.filter((_, i) => i !== index),
    });
  };

  // Follow-up management
  // A date-only picker on a CALENDAR field posts the picked LOCAL day anchored
  // at noon UTC (W10) — never the picker's browser-local-midnight instant,
  // which stored the PREVIOUS day for any positive-offset user and left
  // SoapNoteCard rendering a fabricated time of day. Cleared -> undefined, so
  // the key is dropped rather than posted as ''.
  const handleFollowUpDateChange = (date: Date | null) => {
    onChange({
      ...data,
      followUpDate: datePickerToCalendarValue(date),
    });
  };

  const handleFieldChange = (field: keyof SoapPlan, value: any) => {
    onChange({
      ...data,
      [field]: value,
    });
  };

  // Procedure management
  const handleOpenProcedureDialog = (procedure?: SoapProcedure, index?: number) => {
    if (procedure && index !== undefined) {
      setProcedureData(procedure);
      setEditingProcedureIndex(index);
    } else {
      setProcedureData({ name: '' });
      setEditingProcedureIndex(null);
    }
    setProcedureDialog(true);
  };

  const handleSaveProcedure = () => {
    if (!procedureData.name.trim()) return;

    const procedures = (Array.isArray(data.proceduresPrescribed) ? data.proceduresPrescribed : []);
    let updatedProcedures;

    if (editingProcedureIndex !== null) {
      updatedProcedures = [...procedures];
      updatedProcedures[editingProcedureIndex] = procedureData as SoapProcedure;
    } else {
      updatedProcedures = [...procedures, procedureData as SoapProcedure];
    }

    onChange({
      ...data,
      proceduresPrescribed: updatedProcedures,
    });

    setProcedureDialog(false);
    setProcedureData({ name: '' });
    setEditingProcedureIndex(null);
  };

  const handleDeleteProcedure = (index: number) => {
    const procedures = (Array.isArray(data.proceduresPrescribed) ? data.proceduresPrescribed : []);
    onChange({
      ...data,
      proceduresPrescribed: procedures.filter((_, i) => i !== index),
    });
  };

  return (
    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Diagnostics and Treatments Ordered */}
        <Box>
          <FormLabel sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 'bold', display: 'block' }}>
            Diagnostics and Treatments Ordered
          </FormLabel>
          
          {!disabled && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                fullWidth
                value={newDiagnostic}
                onChange={(e) => setNewDiagnostic(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddDiagnostic();
                  }
                }}
                placeholder="Add diagnostic test (e.g., X-ray, Blood work)..."
                variant="outlined"
              />
              <Button
                variant="outlined"
                onClick={handleAddDiagnostic}
                disabled={!newDiagnostic.trim()}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
          )}

          {data.diagnosticsOrdered && data.diagnosticsOrdered.length > 0 ? (
            <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
              {data.diagnosticsOrdered.map((diagnostic, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={diagnostic}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                  {!disabled && (
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveDiagnostic(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No diagnostics and treatments ordered
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Treatments */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <FormLabel sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
              Treatments & Medications
            </FormLabel>
            {!disabled && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenTreatmentDialog()}
              >
                Add Treatment
              </Button>
            )}
          </Box>

          {/* A vaccine needs its own record (spec §2): the lot and the booster live there, not in the note. */}
          {detectedVaccines.length > 0 && onRecordVaccinations && !disabled && (
            <Alert
              severity="info"
              sx={{ mb: 1.5 }}
              action={
                <Button size="small" variant="contained" onClick={onRecordVaccinations}>
                  Record {detectedVaccines.length === 1 ? 'vaccination' : 'vaccinations'}
                </Button>
              }
            >
              {detectedVaccines.length === 1 ? 'A vaccine is' : `${detectedVaccines.length} vaccines are`} in this plan:{' '}
              {detectedVaccines.join(', ')}. Record {detectedVaccines.length === 1 ? 'it' : 'them'} so the lot and booster
              are tracked.
            </Alert>
          )}

          {(() => {
                // Combine treatments and medications but remove duplicates based on name
                const treatments = Array.isArray(data.treatments) ? data.treatments : [];
                const medications = Array.isArray(data.medicationsPrescribed) ? data.medicationsPrescribed : [];

                // Create a map to track unique items by name (case-insensitive)
                const uniqueItems = new Map();

                // Add treatments first
                treatments.forEach((treatment, index) => {
                  const key = treatment.name.toLowerCase();
                  if (!uniqueItems.has(key)) {
                    uniqueItems.set(key, { ...treatment, sourceIndex: index, source: 'treatments' });
                  }
                });
                
                // Add medications, but only if not already present
                medications.forEach((medication, index) => {
                  const key = medication.name.toLowerCase();
                  if (!uniqueItems.has(key)) {
                    uniqueItems.set(key, { ...medication, sourceIndex: index, source: 'medications' });
                  }
                });
                
                const treatmentRows = Array.from(uniqueItems.values());
                return treatmentRows.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Treatment/Medication</TableCell>
                    <TableCell>Dosage</TableCell>
                    <TableCell>Frequency</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Route</TableCell>
                    {!disabled && <TableCell width={100}>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {treatmentRows.map((treatment, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {treatment.name}
                        {vaccineEntryNames?.has(treatment.name.trim().toLowerCase()) && (
                          <Chip size="small" color="primary" variant="outlined" label="Vaccine" sx={{ ml: 1, height: 20 }} />
                        )}
                      </TableCell>
                      <TableCell>{treatment.dosage || '-'}</TableCell>
                      <TableCell>{treatment.frequency || '-'}</TableCell>
                      <TableCell>{treatment.duration || '-'}</TableCell>
                      <TableCell>{standardizeRouteDisplay(treatment.route)}</TableCell>
                      {!disabled && (
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenTreatmentDialog(treatment, treatment.sourceIndex, treatment.source)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTreatment(treatment.sourceIndex, treatment.source)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No treatments or medications prescribed
            </Typography>
          ); })()}
        </Box>

        <Divider />

        {/* Procedures */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <FormLabel sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
              Procedures
            </FormLabel>
            {!disabled && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenProcedureDialog()}
              >
                Add Procedure
              </Button>
            )}
          </Box>

          {data.proceduresPrescribed && data.proceduresPrescribed.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Procedure Name</TableCell>
                    <TableCell>Notes</TableCell>
                    {!disabled && <TableCell width={100}>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.proceduresPrescribed.map((procedure, index) => (
                    <TableRow key={index}>
                      <TableCell>{procedure.name}</TableCell>
                      <TableCell>{procedure.notes || '-'}</TableCell>
                      {!disabled && (
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenProcedureDialog(procedure, index)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteProcedure(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No procedures prescribed
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Follow-up Date */}
        <Box>
          <FormLabel sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 'bold', display: 'block' }}>
            Follow-up Date
          </FormLabel>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Follow-up Date"
              value={followUpPickerValue(data.followUpDate, browserZone)}
              onChange={handleFollowUpDateChange}
              disabled={disabled}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  InputProps: {
                    startAdornment: <CalendarIcon sx={{ mr: 1 }} />,
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Divider />

        {/* Plan Notes (merged client education and additional notes) */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem', fontWeight: 'bold' }}>
            Plan Notes
          </FormLabel>
          <TextField
            multiline
            rows={5}
            value={data.planNotes || ''}
            onChange={(e) => handleFieldChange('planNotes', e.target.value)}
            disabled={disabled}
            placeholder="Enter plan notes here"
            variant="outlined"
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                lineHeight: 1.6,
              },
            }}
          />
        </FormControl>
      </Box>

      {/* Treatment Dialog */}
      <Dialog open={treatmentDialog} onClose={() => setTreatmentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTreatmentIndex !== null ? 'Edit Treatment' : 'Add Treatment'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              {/* A shortcut, never a restriction (freeSolo): picking a listed vaccine is what makes the
                  "Record vaccination" prompt above match exactly instead of by spelling. */}
              <Autocomplete
                freeSolo
                openOnFocus
                options={treatmentOptions}
                groupBy={(option) => option.group}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                value={treatmentData.name}
                onChange={(_e, picked) =>
                  setTreatmentData({
                    ...treatmentData,
                    name: typeof picked === 'string' ? picked : picked?.label ?? '',
                  })
                }
                inputValue={treatmentData.name}
                onInputChange={(_e, value, reason) => {
                  if (reason === 'input') setTreatmentData({ ...treatmentData, name: value });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Treatment/Medication Name" fullWidth required />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Dosage"
                fullWidth
                value={treatmentData.dosage || ''}
                onChange={(e) => setTreatmentData({ ...treatmentData, dosage: e.target.value })}
                placeholder="e.g., 10mg"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Route"
                fullWidth
                value={treatmentData.route || ''}
                onChange={(e) => setTreatmentData({ ...treatmentData, route: e.target.value })}
                placeholder="e.g., PO, IV, IM"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Frequency"
                fullWidth
                value={treatmentData.frequency || ''}
                onChange={(e) => setTreatmentData({ ...treatmentData, frequency: e.target.value })}
                placeholder="e.g., BID, TID"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Duration"
                fullWidth
                value={treatmentData.duration || ''}
                onChange={(e) => setTreatmentData({ ...treatmentData, duration: e.target.value })}
                placeholder="e.g., 7 days"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={treatmentData.notes || ''}
                onChange={(e) => setTreatmentData({ ...treatmentData, notes: e.target.value })}
                placeholder="Additional instructions or notes..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTreatmentDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveTreatment}
            variant="contained"
            disabled={!treatmentData.name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Procedure Dialog */}
      <Dialog open={procedureDialog} onClose={() => setProcedureDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProcedureIndex !== null ? 'Edit Procedure' : 'Add Procedure'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Procedure Name"
                fullWidth
                required
                value={procedureData.name}
                onChange={(e) => setProcedureData({ ...procedureData, name: e.target.value })}
                placeholder="e.g., Ultrasound, Nasogastric intubation"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={4}
                value={procedureData.notes || ''}
                onChange={(e) => setProcedureData({ ...procedureData, notes: e.target.value })}
                placeholder="Enter additional details about the procedure..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcedureDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveProcedure}
            variant="contained"
            disabled={!procedureData.name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PlanSection;
